import * as React from 'react';
import { LocalDateTime } from 'js-joda';
import clsx from 'clsx';

import { IWithContextProps, withContext } from '@tock/coretypes';
import {
  SLOT_SIDE_LENGTH_REM,
  SLOT_SIDE_LENGTH_REM_SMALL,
  SQUARE_INCREMENT_MINUTES,
} from 'constants/TableBasedTemplatesConstants';
import { getBlockedSquareBackgroundPosition } from 'utils/timeline/timelineStyleCalculations';
import InventorySquareIndicator from 'components/table-based-templates/InventorySquareIndicator';
import PureBaseComponent from '@tock/shared/components/PureBaseComponent';

interface OwnProps {
  additionalClassName?: string;
  className?: string;
  colorClassName?: string;
  columnIndex?: number;
  displayColor?: string;
  isAdditionalInventory?: boolean;
  isSelected?: boolean;
  isSmall?: boolean;
  isBlocked?: boolean;
  isExpanded?: boolean;
  isShowingTurn?: boolean;
  dateTime?: LocalDateTime;
  onClick?(dateTime?: LocalDateTime): void;
  rowIndex?: number;
  status?: string;
  statusModifierClassName?: string;
  turnTimeMinutes?: number;
  text?: React.ReactNode;
  textBottomLeft?: React.ReactNode;
  textBottomRight?: React.ReactNode;
  textTopLeft?: React.ReactNode;
}

interface State {
  initiallyShowingOverlay: boolean;
  shouldShowOverlay: boolean;
}

type Props = OwnProps & IWithContextProps;

class InventorySquare extends PureBaseComponent<Props, State> {
  static defaultProps = {
    className: 'InventorySquare',
  };

  constructor(props: Props) {
    super(props);
    const { turnTimeMinutes, isSelected, isExpanded } = props;
    const showOverlay = !!(turnTimeMinutes && isSelected && isExpanded);

    this.state = {
      initiallyShowingOverlay: showOverlay,
      shouldShowOverlay: false,
    };
  }

  componentDidMount() {
    const { initiallyShowingOverlay } = this.state;

    if (initiallyShowingOverlay) {
      this.setState((prevState: State) => ({
        shouldShowOverlay: true,
      }));
    }
  }

  handleClick() {
    const { dateTime, onClick } = this.props;
    if (onClick) {
      onClick(dateTime);
    }
  }

  render() {
    const {
      additionalClassName,
      className: classNameFromProps,
      colorClassName,
      columnIndex,
      displayColor,
      isAdditionalInventory = false,
      isSelected,
      isSmall,
      isShowingTurn,
      isBlocked,
      isExpanded,
      rowIndex,
      status,
      statusModifierClassName,
      text,
      textBottomLeft,
      textBottomRight,
      textTopLeft,
      turnTimeMinutes,
    } = this.props;

    const { initiallyShowingOverlay, shouldShowOverlay } = this.state;

    const showOverlay = initiallyShowingOverlay
      ? shouldShowOverlay
        ? true
        : false
      : !!(turnTimeMinutes && isSelected && isExpanded);

    const className = clsx(colorClassName, classNameFromProps, additionalClassName, {
      'has-color': !!colorClassName,
      'has-custom-color': !!displayColor,
      'is-showing-turn': isShowingTurn,
      'is-selected': isSelected,
      'is-overlay': showOverlay,
      'is-blocked': !showOverlay && isBlocked,
    });

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const numIncrements = showOverlay ? turnTimeMinutes! / SQUARE_INCREMENT_MINUTES : undefined;
    let width;

    let style: React.CSSProperties = {
      backgroundColor: displayColor,
      backgroundPosition:
        isBlocked && columnIndex !== undefined
          ? getBlockedSquareBackgroundPosition({
              isAdditionalInventory,
              rowIndex,
              squaresToOffset: columnIndex,
            }).backgroundPosition
          : undefined,
    };

    if (numIncrements) {
      width = `${
        (isSmall ? SLOT_SIDE_LENGTH_REM_SMALL : SLOT_SIDE_LENGTH_REM) * numIncrements - 0.1
      }rem`;
      style = {
        ...style,
        width,
        maxWidth: width,
        position: 'absolute',
        left: 0,
        zIndex: 1,
      };
    }

    return (
      <div className={className} onClick={this.handleClick} style={style}>
        {status && (
          <InventorySquareIndicator
            isSelected={isSelected}
            statusModifierClassName={statusModifierClassName}
            statusText={status}
          />
        )}
        {textTopLeft && <div className="InventorySquare-topLeftText">{textTopLeft}</div>}

        {text && <div className="InventorySquare-content">{text}</div>}

        {textBottomLeft && <div className="InventorySquare-bottomLeftText">{textBottomLeft}</div>}

        {textBottomRight && (
          <div className="InventorySquare-bottomRightText">{textBottomRight}</div>
        )}
      </div>
    );
  }
}

export default withContext(InventorySquare);
